import {
  DynamicReportBody,
  ReportColumn,
  ReportEditorColumn,
  ReportField,
  ReportFormErrors,
  ReportFormValues,
} from '../types/report-builder.types';

export const REPORT_BODY_MAX_LENGTH = 2047;
export const REPORT_NAME_MAX_LENGTH = 127;
export const REPORT_COMMENT_MAX_LENGTH = 1023;

const DATE_FIELDS = new Set(['executedDate', 'createdDate', 'lastModifiedDate', 'plannedDate', 'startDate']);
const NUMBER_FIELDS = new Set(['srId', 'contractCounter']);
const BOOLEAN_FIELDS = new Set(['archived', 'onRevision']);
const EXTRA_FIELDS: ReportField[] = [
  {key: 'statusCode', label: 'Код статуса', type: 'text', canFilter: true},
  {key: 'serviceTypeCode', label: 'Код услуги', type: 'text', canFilter: true},
  {key: 'manager', label: 'ID менеджера', type: 'text', canFilter: true},
  {key: 'managerLogin', label: 'Логин менеджера', type: 'text', canFilter: true},
  {key: 'workers', label: 'ID исполнителей', type: 'text', canFilter: true},
];
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(\.\d{1,9})?)?$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(\.\d{1,9})?)?(?:Z|[+-]\d{2}:\d{2})$/i;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const countReportCharacters = (value: string): number => Array.from(value).length;
export const getReportBodyLength = (body: DynamicReportBody): number => countReportCharacters(JSON.stringify(body));

const getFieldType = (key: string, schema: Record<string, unknown> = {}): ReportField['type'] => {
  if (DATE_FIELDS.has(key) || schema.format === 'date-time') return 'date';
  if (NUMBER_FIELDS.has(key)) return 'number';
  if (BOOLEAN_FIELDS.has(key)) return 'boolean';
  const types = Array.isArray(schema.type) ? schema.type : [schema.type];
  if (types.includes('boolean')) return 'boolean';
  if (types.includes('integer') || types.includes('number')) return 'number';
  return 'text';
};

export const getReportFields = (schema: unknown): ReportField[] => {
  if (!isObject(schema) || !isObject(schema.properties) || !Object.keys(schema.properties).length) {
    throw new Error('Не удалось прочитать модель полей отчета.');
  }
  const fields = Object.entries(schema.properties).map(([key, property]) => {
    const metadata = isObject(property) ? property : {};
    return {
      key,
      label: typeof metadata.description === 'string' && metadata.description.trim() ? metadata.description : key,
      type: getFieldType(key, metadata),
      canFilter: key !== 'workersFio',
    };
  });
  return [...fields, ...EXTRA_FIELDS.filter(({key}) => !fields.some((field) => field.key === key))];
};

export const createReportColumn = (key: string): ReportEditorColumn => ({
  key,
  filterMode: 'none',
  value: '',
  minValue: '',
  maxValue: '',
});

const pad = (value: number, size = 2) => String(value).padStart(size, '0');

// Native datetime-local inputs accept at most millisecond precision.
export const reportDateToLocal = (value: string): string => {
  if (!ISO_DATE_PATTERN.test(value)) throw new Error('Дата должна содержать время и часовой пояс.');
  const date = new Date(value);
  const sourceDate = value.slice(0, 10);
  const calendarDate = new Date(`${sourceDate}T00:00:00Z`);
  if (
    Number.isNaN(date.getTime()) ||
    Number.isNaN(calendarDate.getTime()) ||
    calendarDate.toISOString().slice(0, 10) !== sourceDate
  ) {
    throw new Error('Некорректная дата.');
  }
  const fraction = value.match(ISO_DATE_PATTERN)?.[1]?.slice(0, 4) || '';
  return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${fraction}`;
};

export const reportDateToIso = (value: string): string => {
  const match = value.match(LOCAL_DATE_PATTERN);
  if (!match) throw new Error('Укажите корректные дату и время.');
  const [, year, month, day, hour, minute, second = '00', fraction = ''] = match;
  const date = new Date(value);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== Number(year) ||
    date.getMonth() + 1 !== Number(month) ||
    date.getDate() !== Number(day) ||
    date.getHours() !== Number(hour) ||
    date.getMinutes() !== Number(minute) ||
    date.getSeconds() !== Number(second)
  ) {
    throw new Error('Укажите корректные дату и время.');
  }
  return date.toISOString().replace(/\.\d{3}Z$/, `${fraction || '.000'}Z`);
};

// Keep the exact server instant (including sub-milliseconds and DST offset) for unchanged bounds.
const getDateFilterValue = (value: string, original?: string): string =>
  original && value === reportDateToLocal(original) ? original : reportDateToIso(value);

const isLong = (value: string): boolean => {
  if (!/^[+-]?\d+$/.test(value)) return false;
  const number = BigInt(value);
  return number >= BigInt('-9223372036854775808') && number <= BigInt('9223372036854775807');
};

export const getColumnError = (column: ReportEditorColumn, field?: ReportField): string | undefined => {
  if (!field) return 'Поле отсутствует в модели отчета. Удалите его или обновите модель.';
  if (column.filterMode === 'none') return undefined;
  if (!field.canFilter) return 'Для этого поля фильтр недоступен.';
  const value = column.value.trim();
  const min = column.minValue.trim();
  const max = column.maxValue.trim();
  if (column.filterMode === 'value') {
    if (field.type === 'date') return 'Для даты используйте диапазон с начальной датой.';
    if (!value) return 'Укажите значение фильтра.';
    if (value.split(',').some((part) => !part.trim() || part.trim() === 'null')) {
      return 'Укажите непустые значения через запятую; null не является пустым фильтром.';
    }
    if (field.type === 'number' && !value.split(',').every((part) => isLong(part.trim()))) {
      return 'Укажите целые числа от −9223372036854775808 до 9223372036854775807.';
    }
    if (field.type === 'boolean' && value !== 'true' && value !== 'false') return 'Выберите «Да» или «Нет».';
    if (
      ['manager', 'workers'].includes(field.key) &&
      !value.split(',').every((part) => UUID_PATTERN.test(part.trim()))
    ) {
      return 'Укажите корректные UUID через запятую.';
    }
    return undefined;
  }
  if (column.filterMode !== 'range') return 'Неизвестный режим фильтра.';
  if (field.type === 'boolean') return 'Для логического поля доступен только фильтр по значению.';
  if (!min && !max) return 'Укажите хотя бы одну границу диапазона.';
  if (min === 'null' || max === 'null') return 'null не является пустым фильтром.';
  if (field.type === 'date') {
    if (!min) return 'Для даты обязательна начальная граница диапазона.';
    try {
      const minIso = getDateFilterValue(min, column.originalMinValue);
      const maxIso = max ? getDateFilterValue(max, column.originalMaxValue) : '';
      if (maxIso && new Date(minIso).getTime() > new Date(maxIso).getTime()) return 'Начало периода позже окончания.';
    } catch (error) {
      return error instanceof Error ? error.message : 'Некорректная дата.';
    }
  }
  if (field.type === 'number') {
    if ((min && !isLong(min)) || (max && !isLong(max))) return 'Границы должны быть целыми числами в диапазоне Long.';
    if (min && max && BigInt(min) > BigInt(max)) return 'Минимальное значение больше максимального.';
  }
  return undefined;
};

export const createReportBody = (columns: ReportEditorColumn[], fields: ReportField[]): DynamicReportBody => {
  if (!columns.length) throw new Error('Добавьте хотя бы одну колонку отчета.');
  const entries = columns.map((column, index): [string, ReportColumn] => {
    const field = fields.find(({key}) => key === column.key);
    const error = getColumnError(column, field);
    if (error) throw new Error(`${field?.label || column.key}: ${error}`);
    if (columns.findIndex(({key}) => key === column.key) !== index)
      throw new Error('Колонки отчета не должны повторяться.');
    const result: ReportColumn = {position: index + 1};
    if (column.isConstant !== undefined) result.isConstant = column.isConstant;
    if (column.filterMode === 'value') {
      result.value = field?.type === 'boolean' ? column.value.trim() === 'true' : column.value.trim();
    }
    if (column.filterMode === 'range') {
      if (column.minValue.trim()) {
        result.minValue =
          field?.type === 'date'
            ? getDateFilterValue(column.minValue.trim(), column.originalMinValue)
            : column.minValue.trim();
      }
      if (column.maxValue.trim()) {
        result.maxValue =
          field?.type === 'date'
            ? getDateFilterValue(column.maxValue.trim(), column.originalMaxValue)
            : column.maxValue.trim();
      }
    }
    return [column.key, result];
  });
  return Object.fromEntries(entries);
};

export const readReportColumns = (body: unknown, fields: ReportField[]): ReportEditorColumn[] => {
  if (!isObject(body) || !Object.keys(body).length)
    throw new Error('Шаблон содержит некорректное или пустое тело отчета.');
  const entries = Object.entries(body);
  return entries.map(([key, value], index) => {
    const field = fields.find((item) => item.key === key);
    if (!field) throw new Error(`Поле «${key}» отсутствует в модели. Этот шаблон нельзя открыть без потери данных.`);
    if (!isObject(value) || value.position !== index + 1) {
      throw new Error(`Некорректный порядок колонки «${field.label}».`);
    }
    if (
      Object.keys(value).some((name) => !['position', 'value', 'minValue', 'maxValue', 'isConstant'].includes(name))
    ) {
      throw new Error(`Колонка «${field.label}» содержит неподдерживаемые параметры.`);
    }
    if ('isConstant' in value && typeof value.isConstant !== 'boolean') {
      throw new Error(`Некорректные параметры колонки «${field.label}».`);
    }
    const column = createReportColumn(key);
    if (typeof value.isConstant === 'boolean') column.isConstant = value.isConstant;
    const readFilter = (name: 'value' | 'minValue' | 'maxValue'): string => {
      if (!(name in value)) return '';
      const filter = value[name];
      if (
        !['string', 'number', 'boolean'].includes(typeof filter) ||
        (typeof filter === 'boolean' && name !== 'value')
      ) {
        throw new Error(`Некорректный фильтр колонки «${field.label}».`);
      }
      if (typeof filter === 'number' && (!Number.isFinite(filter) || !Number.isSafeInteger(filter))) {
        throw new Error(`Числовой фильтр «${field.label}» невозможно прочитать без потери точности.`);
      }
      return String(filter);
    };
    column.value = readFilter('value');
    column.minValue = readFilter('minValue');
    column.maxValue = readFilter('maxValue');
    if (column.value && (column.minValue || column.maxValue)) {
      throw new Error(
        `Колонка «${field.label}» одновременно содержит значение и диапазон. Изменение потеряет часть фильтра.`,
      );
    }
    column.filterMode = column.value ? 'value' : column.minValue || column.maxValue ? 'range' : 'none';
    if (field.type === 'date') {
      if (column.minValue) {
        column.originalMinValue = column.minValue;
        column.minValue = reportDateToLocal(column.minValue);
      }
      if (column.maxValue) {
        column.originalMaxValue = column.maxValue;
        column.maxValue = reportDateToLocal(column.maxValue);
      }
    }
    const error = getColumnError(column, field);
    if (error) throw new Error(`${field.label}: ${error}`);
    return column;
  });
};

export const validateReportForm = (values: ReportFormValues, fields?: ReportField[]): ReportFormErrors => {
  const errors: ReportFormErrors = {columnErrors: {}};
  if (!values.name.trim()) errors.name = 'Введите название отчета.';
  else if (countReportCharacters(values.name.trim()) > REPORT_NAME_MAX_LENGTH) errors.name = 'Не более 127 символов.';
  if (countReportCharacters(values.comment) > REPORT_COMMENT_MAX_LENGTH) errors.comment = 'Не более 1023 символов.';
  if (!values.columns.length) errors.columns = 'Добавьте хотя бы одну колонку отчета.';
  const availableFields =
    fields ||
    values.columns.map(({key}) => ({key, label: key, type: getFieldType(key), canFilter: key !== 'workersFio'}));
  values.columns.forEach((column, index) => {
    const error = getColumnError(
      column,
      availableFields.find(({key}) => key === column.key),
    );
    if (error) errors.columnErrors[column.key] = error;
    if (values.columns.findIndex(({key}) => key === column.key) !== index)
      errors.columns = 'Колонки не должны повторяться.';
  });
  if (!errors.columns && !Object.keys(errors.columnErrors).length) {
    const body = createReportBody(values.columns, availableFields);
    if (getReportBodyLength(body) > REPORT_BODY_MAX_LENGTH) {
      errors.body = 'Настройки отчета превышают лимит 2047 символов. Сократите количество колонок или фильтры.';
    }
  }
  return errors;
};
